import { useState } from 'react'
import { Alert, Button, Card, Col, Descriptions, Flex, Result, Row, Skeleton, Space, Tabs, Tag, Tooltip, Typography } from 'antd'
import { EyeInvisibleOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useRadiusRealm } from '@/shared/api/reference'
import { AgentRef } from '@/features/agent/AgentRef'
import { ContactCard } from '@/features/contact/ContactCard'
import { ContactRevealControl } from '@/features/contact/ContactRevealControl'
import { SubscriberActions } from '@/features/subscriber/SubscriberActions'
import { SubscriberGrants } from '@/features/subscriber/SubscriberGrants'
import { SubscriberContracts } from '@/features/contract/SubscriberContracts'
import { SubscriberFundAction } from '@/features/subscriber/SubscriberFundAction'
import { SubscriberQuickLinks } from '@/features/grant/SubscriberQuickLinks'
import { SubscriberOverviewCard } from '@/features/subscriber/SubscriberOverviewCard'
import { SubscriberProfileCard } from '@/features/subscriber/SubscriberProfileCard'
import { ChangeVispModal } from '@/features/subscriber/ChangeVispModal'
import { ChangeBranchModal } from '@/features/branch/ChangeBranchModal'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useSubscriberOverview } from '@/features/subscriber/useSubscriberOverview'
import { useSubscriberPhoneReveal, type SubscriberPhoneReveal } from '@/features/subscriber/useSubscriberPhoneReveal'
import { fetchSubscriber, type Subscriber } from '@/features/subscriber/subscriberApi'
import { SessionHistoryTable } from '@/features/session/SessionHistoryTable'
import { SubscriberSessions } from '@/features/session/SubscriberSessions'
import { SelfcarePasswordAction } from '@/features/selfcare/SelfcarePasswordAction'
import { SubscriberIpAllocations } from '@/features/inventory/SubscriberIpAllocations'
import { TelegramLinkPanel } from '@/features/notify/TelegramLinkPanel'
import { SubscriberTrafficPanel } from '@/features/reports/SubscriberTrafficPanel'
import { ReceiptTable } from '@/features/cashdesk/ReceiptTable'
import { SubscriberInvoices } from '@/features/invoice/SubscriberInvoices'
import { SubscriberHistory } from '@/features/subscriber/history/SubscriberHistory'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { radiusUsername } from '@/shared/lib/radius'
import { OnlineBadge, SubscriberStateTag } from '@/shared/ui/StatusTag'
import { PageHeader } from '@/shared/ui/PageHeader'

const TABS = ['overview', 'sessions', 'traffic', 'services', 'receipts', 'history', 'contract', 'invoices'] as const
type TabKey = (typeof TABS)[number]

function isTabKey(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value)
}

/**
 * Abonent kartochkasi.
 *
 * Bu sahifa bir necha FEATURE'ni yig'adi (`subscriber`, `session`,
 * `selfcare`, `notify`, `reports` va katalog ma'lumotnomasi) — bu `pages/`
 * ning vazifasi (07-FRONTEND.md §6). Feature'larning o'zi bir-birini
 * import qilmaydi.
 *
 * <h2>⚠️ NEGA BO'LIMLARGA AJRATILDI — tartib emas, TEZLIK masalasi</h2>
 * Ilgari hamma karta bitta uzun ustunda turardi va kartochka ochilganda
 * 13 ta so'rov BIRDANIGA ketardi (tester hisobotidagi asosiy shikoyat).
 * Ularning ko'pchiligi ekranning pastida — operator ko'pincha
 * aylantirib ham bormaydigan joyda — edi: sessiyalar tarixi, kunlik
 * trafik jadvali, kabinet paroli holati, Telegram bog'lanishi. Ya'ni eng
 * og'ir so'rovlar eng kam so'raladigan ma'lumot uchun yuborilar va ENG
 * KERAKLI kartalarni (balans, paket, tezlik) kutib qoldirardi.
 *
 * ⚠️ HECH NARSA OLIB TASHLANMADI — hammasi o'z bo'limida joyida turibdi.
 * `Tabs` bo'lim mazmunini FAQAT birinchi ochilganda chizadi (rc-tabs
 * ko'rilmagan panelni umuman mount qilmaydi), ya'ni so'rov ham o'shanda
 * ketadi. Bir marta ochilgach bo'lim keshda qoladi va qayta-qayta
 * so'ralmaydi.
 *
 * ⚠️ Faol bo'lim URL'da (`?tab=...`) — `SessionsPage` bilan bir xil
 * andoza: sahifa yangilanganda yoki havola ulashilganda o'sha bo'lim
 * ochiladi, aks holda "sessiyalar tarixining 2-sahifasi" havolasi qabul
 * qiluvchida butunlay boshqa ekranni ochardi.
 */
export function SubscriberDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { can } = usePermissions()
  const canViewReceipts = can('cash.receipt.view')
  const canViewInvoices = can('invoice.view')
  const canViewHistory = can('subscriber.history.view')
  const canViewContracts = can('subscriber.contract.view')

  const requestedTab = searchParams.get('tab')
  const activeTab: TabKey =
    isTabKey(requestedTab) &&
    (requestedTab !== 'receipts' || canViewReceipts) &&
    (requestedTab !== 'invoices' || canViewInvoices) &&
    (requestedTab !== 'history' || canViewHistory) &&
    (requestedTab !== 'contract' || canViewContracts)
      ? requestedTab
      : 'overview'

  const query = useQuery({
    queryKey: queryKeys.subscribers.detail(id ?? ''),
    queryFn: () => fetchSubscriber(id ?? ''),
    enabled: id !== undefined,
    /*
      Yozuvning O'ZI (login, tur, holat) kamdan-kam o'zgaradi va har
      o'zgarish keshni bekor qiladi (`SubscriberActions`,
      `EditSubscriberModal`). Ya'ni eskirish INVALIDATSIYA orqali
      boshqariladi, vaqt orqali emas — ro'yxatdan kartochkaga kirib-chiqib
      yurganda umumiy 10 soniyalik chegara ortiqcha so'rov yuborardi.
    */
    staleTime: 60_000,
  })

  /*
    ⚠️ HOLAT SAHIFADA, KARTA ICHIDA EMAS — va bu MAJBURIY.

    Uni ikkita karta o'qiydi: chapdagisi raqamni ochadi, o'ngdagisi esa
    «shu ekranda raqam OCHIQ turibdimi» degan savolga javob oladi
    (`ContactCard.openPhone`). Holat chap karta ichida qolsa, o'ng karta
    raqam niqoblanganda ham «kartada ochiq» deb yozib turardi — ya'ni
    ikkala qiymat ham yashiringan bo'lardi.

    ⚠️ 2026-08-24: bu endi MAHALLIY BELGI EMAS. Server raqamni o'zi
    niqoblaydi va ochish `POST /api/subscribers/{id}/contact/reveal`
    orqali — sabab bilan va auditga yozilib. Ya'ni ochilgan qiymat
    SO'RALADI, va u keshda saqlanmaydi (hook javadoc'i).
  */
  const phone = useSubscriberPhoneReveal(id ?? '', query.data?.phone ?? null)

  if (!id) {
    return <Result status="404" title={t('notFound.title')} subTitle={t('notFound.text')} />
  }

  if (query.isPending) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 6 }} />
      </Card>
    )
  }

  if (query.isError) {
    return (
      <Result
        status="error"
        title={t('common.error')}
        subTitle={translateError(query.error, t)}
        extra={<Link to="/subscribers">{t('subscriber.backToList')}</Link>}
      />
    )
  }

  const subscriber = query.data
  const isClosed = subscriber.state === 'CLOSED'

  return (
    <>
      <PageHeader
        title={subscriber.login}
        description={subscriber.displayName ?? undefined}
        /* ⚠️ Ikki o'tish havolasi AMALLARDAN OLDIN (SHARTNOMA-2 §C):
           «paketlari» va «to'lovlari» — ko'rish, amal emas. Ular
           `SubscriberActions` ichiga qo'yilsa uch nuqta menyusida
           yashirinardi va operator ularni topmasdi. */
        extra={
          <>
            <SubscriberQuickLinks subscriberId={subscriber.id} />
            <SubscriberActions subscriber={subscriber} />
          </>
        }
      />

      {isClosed ? (
        // Yopilgan abonent terminal holatda: uni qaytarib bo'lmaydi va
        // amallar tugmalari ham chizilmaydi. Buni aytmaslik operatorni
        // "nega tugmalar yo'q?" degan savolga olib kelardi.
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('subscriber.closedTitle')}
          description={t('subscriber.closedHint')}
        />
      ) : null}

      <Tabs
        activeKey={activeTab}
        onChange={(key) =>
          setSearchParams(
            (current) => {
              const next = new URLSearchParams(current)
              // Standart bo'limda parametr yozilmaydi — manzil toza qolsin
              if (key === 'overview') next.delete('tab')
              else next.set('tab', key)
              return next
            },
            // Bo'lim almashishi tarixga yangi yozuv qo'shmasin: "orqaga"
            // tugmasi kartochkadan CHIQISHI kutiladi
            { replace: true },
          )
        }
        items={[
          {
            key: 'overview',
            label: t('subscriber.tabs.overview'),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <SubscriberBasicInfo subscriber={subscriber} phone={phone} />
                </Col>

                {/*
                  ⚠️ "Asosiy ma'lumot" YONIDA, alohida bo'limda EMAS.
                  Viloyat, tuman, manzil va e-pochta ilgari faqat
                  tahrirlash oynasida ko'rinardi — ya'ni "abonent qayerda
                  yashaydi?" degan savolga javob olish uchun O'ZGARTIRISH
                  formasini ochish kerak edi. Ularni bo'limga yashirish
                  o'sha nuqsonni takrorlardi, faqat boshqa nom bilan.
                */}
                <Col xs={24} lg={12}>
                  {/*
                    ⚠️ `openPhone` — QIYMAT EMAS, HOLAT: «shu ekranda
                    raqam OCHIQ turibdimi». Turgan bo'lsa o'ng kartadagi
                    niqob hech narsani himoya qilmaydi — bitta ekranda
                    bitta raqam ham ochiq, ham niqoblangan bo'lib
                    ko'rinardi va `contact.reveal` auditi chetlab
                    o'tilgandek bo'lardi (`ContactCard` javadoc'i).

                    ⚠️ 2026-08-24 dan shart SHARTLI: chap kartadagi raqam
                    endi SUKUT BO'YICHA NIQOBLANGAN (egasi talabi), ya'ni
                    u faqat operator «Ko'rsatish» ni bosgan paytda ochiq
                    turadi. `subscriber.phone` ni shartsiz uzatish endi
                    YOLG'ON bo'lardi: o'ng karta «kartada ochiq turibdi»
                    deb yozib, ikkala qiymatni ham yashirardi.
                  */}
                  <ContactCard
                    ownerType="SUBSCRIBER"
                    ownerId={subscriber.id}
                    openPhone={phone.revealed ? phone.shown : null}
                  />
                </Col>

                <Col span={24}>
                  <SubscriberProfileCard subscriberId={subscriber.id} profile={subscriber.profile} />
                </Col>

                <Col span={24}>
                  {/*
                    ⚠️ `active` — KO'RINISH emas, YANGILANISH kaliti.

                    Karta o'zini 15 soniyada qayta so'raydi. Boshqa bo'limga
                    o'tilganda u mount holida QOLADI (bo'lim keshi shunday
                    ishlaydi) va so'rov davom etaverardi. Ko'rinmaydigan
                    kartani so'rab turish sof isrof — ayniqsa operator
                    kartochkani soatlab ochiq qoldiradigan ish tartibida.
                  */}
                  <SubscriberOverviewCard
                    subscriberId={subscriber.id}
                    active={activeTab === 'overview'}
                  />
                </Col>

                <Col span={24}>
                  <SubscriberGrants
                    subscriberId={subscriber.id}
                    subscriberKind={subscriber.kind}
                    branchNodeId={subscriber.branchNodeId ?? null}
                  />
                </Col>
              </Row>
            ),
          },
          {
            key: 'sessions',
            label: t('subscriber.tabs.sessions'),
            children: (
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <SubscriberSessions
                    username={subscriber.login}
                    active={activeTab === 'sessions'}
                  />
                </Col>
                <Col span={24}>
                  <Card title={t('subscriber.sessionHistory')}>
                    <Flex vertical gap="middle">
                      <SessionHistoryTable username={subscriber.login} urlPrefix="h" />
                    </Flex>
                  </Card>
                </Col>
              </Row>
            ),
          },
          {
            key: 'traffic',
            label: t('subscriber.tabs.traffic'),
            /*
              ⚠️ Sessiyalar tarixidan ALOHIDA bo'lim. "Qancha sarfladi" va
              "qachon ulandi" bir xil sonni ko'rsatishi SHART EMAS —
              manbalari boshqa (`SubscriberTrafficPanel` javadoc'i). Ular
              yonma-yon turganda operator farqni nosozlik deb o'qirdi.
            */
            children: <SubscriberTrafficPanel subscriberId={subscriber.id} />,
          },
          {
            key: 'services',
            label: t('subscriber.tabs.services'),
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <SubscriberFundAction subscriberId={subscriber.id} disabled={isClosed} />
                </Col>
                <Col xs={24} lg={12}>
                  <SelfcarePasswordAction subscriberId={subscriber.id} disabled={isClosed} />
                </Col>
                <Col xs={24} lg={12}>
                  <TelegramLinkPanel subscriberId={subscriber.id} disabled={isClosed} />
                </Col>
                {/*
                  ⚠️ To'liq enlikda — jadval va uning ogohlantirishlari
                  yarim ustunga sig'maydi. Bu shakl masalasi emas:
                  nomuvofiqlik ogohlantirishi (`staticIp` reyestrda yo'q)
                  siqilib ikki qatorga bo'linsa operator uni sarlavha
                  deb o'qib o'tib ketardi.
                */}
                <Col span={24}>
                  {/* ⚠️ TO'LIQ yozuv beriladi: «statik IP qilib qo'yish»
                      amali `PUT` ni to'liq almashtirish sifatida
                      yuboradi (`SubscriberIpAllocations` javadoc'i). */}
                  <SubscriberIpAllocations subscriber={subscriber} disabled={isClosed} />
                </Col>
              </Row>
            ),
          },
          ...(canViewReceipts
            ? [
                {
                  key: 'receipts',
                  label: t('subscriber.tabs.receipts'),
                  children: (
                    <ReceiptTable
                      filter={{ subscriberId: subscriber.id }}
                      tableId="subscriber-receipts"
                      urlPrefix="r"
                    />
                  ),
                },
              ]
            : []),
          ...(canViewHistory
            ? [
                {
                  key: 'history',
                  label: t('subscriber.tabs.history'),
                  children: <SubscriberHistory subscriberId={subscriber.id} login={subscriber.login} />,
                },
              ]
            : []),
          ...(canViewContracts
            ? [
                {
                  key: 'contract',
                  label: t('subscriber.tabs.contract'),
                  children: <SubscriberContracts subscriberId={subscriber.id} />,
                },
              ]
            : []),
          ...(canViewInvoices
            ? [
                {
                  key: 'invoices',
                  label: t('subscriber.tabs.invoices'),
                  children: <SubscriberInvoices subscriberId={subscriber.id} />,
                },
              ]
            : []),
        ]}
      />
    </>
  )
}

/**
 * "Asosiy ma'lumot" kartasi.
 *
 * ⚠️ ALOHIDA KOMPONENT — bu shakl masalasi emas, SO'ROV masalasi.
 * Karta ikkita qo'shimcha ma'lumotga tayanadi: RADIUS realm va ulanish
 * holati. Ular sahifaning ILDIZIDA so'ralsa, operator qaysi bo'limda
 * turishidan qat'i nazar yuboriladi — ya'ni to'g'ridan-to'g'ri
 * `?tab=sessions` havolasi bilan kirgan odam ham ularni tortardi.
 * Komponent ichida so'ralganda esa so'rov faqat karta CHIZILGANDA ketadi.
 */
function SubscriberBasicInfo({
  subscriber,
  phone,
}: {
  subscriber: Subscriber
  phone: SubscriberPhoneReveal
}) {
  const { t } = useTranslation()
  const radiusRealm = useRadiusRealm()
  const { can, canAll } = usePermissions()
  const { user } = useAuth()
  const [changingVisp, setChangingVisp] = useState(false)
  const [changingBranch, setChangingBranch] = useState(false)
  const { options: branchOptions } = useActiveBranches()
  const canChangeBranch = can('subscriber.update') && branchOptions.length > 0
  const tenantWide = user?.scopes.some((scope) => scope.type === 'TENANT' || scope.type === 'GLOBAL') ?? false
  const branchName = branchOptions.find((b) => b.id === subscriber.branchNodeId)?.name
  const canChangeVisp = canAll('subscriber.visp.assign', 'network.visp.read')

  /*
    ⚠️ ULANISH BELGISI "Umumiy manzara" SO'ROVIDAN OLINADI — QO'SHIMCHA
    so'rov yuborilmaydi.

    Ilgari bu yerda `useOnlineLogins()` turardi: u har 15 soniyada
    `GET /api/sessions/active?limit=500` ni — ya'ni BUTUN tashkilotning
    faol sessiyalarini — tortib, undan bitta `true/false` ni olardi.
    Javobning qolgan qismi hech qayerda ishlatilmasdi.

    `overview.activeSessions` aynan shu abonentning ochiq sessiyalari
    soni (backend: `SubscriberServiceImpl.overview` → `activeSessionCount`),
    ya'ni javob bir xil. So'rov esa pastdagi "Umumiy manzara" kartasi
    uchun baribir yuboriladi va TanStack ikkalasini bitta kalitda
    birlashtiradi (`useSubscriberOverview`).

    ⚠️ `isSuccess` sharti SAQLANDI: so'rov yiqilsa belgi CHIZILMAYDI.
    "Oflayn" deb ko'rsatish operatorni butunlay boshqa yo'nalishga
    (parol, kabel, MikroTik sozlamalari) boshlab ketardi — holbuki
    abonent onlayn, faqat panel ma'lumotni ololmagan.
  */
  const overview = useSubscriberOverview(subscriber.id)

  return (
    <Card title={t('subscriber.basicInfo')}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('subscriber.username')}>{subscriber.login}</Descriptions.Item>
        {/* ⚠️ ABONENT AYNAN SHUNI TERADI.
            X-SIB ichida login toza (`room101`), RADIUS'ga esa
            `room101@uz-venue` yoziladi — FreeRADIUS jadvallarida
            tenant ustuni yo'q va realm yagona ajratuvchi.
            Buni ko'rsatmaslik eng qimmat nosozlikni tug'diradi:
            operator `room101` ni beradi, abonent ulanolmaydi,
            panelda esa hech qanday belgi bo'lmaydi. */}
        {radiusRealm ? (
          <Descriptions.Item label={t('subscriber.radiusLogin')}>
            <Typography.Text copyable code>
              {radiusUsername(subscriber.login, radiusRealm)}
            </Typography.Text>
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label={t('subscribers.columns.status')}>
          <Space>
            <SubscriberStateTag state={subscriber.state} />
            {overview.isSuccess ? <OnlineBadge online={overview.data.activeSessions > 0} /> : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('subscriber.phone')}>
          {/*
            ⚠️ «—» EMAS, aniq matn: raqamsiz abonentga SMS bormaydi va
            bu operator ko'rishi kerak bo'lgan HOLAT. Chiziqcha uni
            "bu maydon bo'sh" darajasiga tushirardi va hech kim
            to'ldirmasdi.

            =============================================================
            ⚠️ NIQOB — EGASI QARORI (2026-08-24)
            =============================================================
            Ilgari raqam bu yerda TO'LIQ turardi, izohda esa «chegara —
            ko'rinish doirasi, niqob emas» deb yozilgan edi. Egasi
            ekrandan ko'rib boshqacha qaror qildi: «ikkalasini ham mask
            qil, ko'rish uchun description yozsin».

            ⚠️ VA QAROR IKKI BOSQICHDA KELDI. Birinchi bosqichda niqob
            faqat PANELDA edi (qiymat javobda to'liq kelardi), shuning
            uchun «ko'rsatish» mahalliy belgi bo'lgan va huquq bilan
            to'silmagan edi — panel serverdan qattiqroq bo'lmasin degan
            sabab bilan. Egasi keyin serverning O'ZINI niqoblashga qaror
            qildi va `POST /api/subscribers/{id}/contact/reveal` yo'lini
            qo'shdirdi.

            ⛔ ENDI DALIL TESKARI: qiymat javobda NIQOBLANGAN keladi,
            ochish `contact.reveal` + abonent doirasi + MAJBURIY sabab
            + `CONTACT_PHONE_REVEAL` auditi bilan bo'ladi. Ya'ni tugmani
            shu huquq ostiga olish panelni qattiqroq emas, server bilan
            BIR XIL qiladi. Huquq yo'q bo'lsa tugma UMUMAN chizilmaydi
            (`ContactCard` naqshi) — bosilganda 403 beradigan tugma bu
            kod bazasida rad etilgan.

            ⚠️ O'TISH DAVRI: backend niqoblashni bosqichma-bosqich
            yoqadi. Qiymat hali TO'LIQ kelayotgan bo'lsa (`locked`
            `false`) panel uni shundayligicha ko'rsatadi va ochish
            taklif qilmaydi — yo'q niqobni «ochish» auditni bo'sh
            yozuvlar bilan to'ldirardi.
          */}
          {subscriber.phone ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space size={8} wrap>
                {/*
                  ⚠️ KO'RINISH `LeadsPage` NAQSHIDAN — yangi uslub o'ylab
                  topilmadi: niqoblangan matn, yonida ko'z belgili
                  «Raqamni ochish», ochilgach nusxa olinadigan qiymat va
                  «Ochilgan» yorlig'i. Endi MEXANIZM ham o'sha:
                  serverdan sabab bilan so'raladi va auditga tushadi.
                */}
                {phone.locked ? (
                  <>
                    {/* ⚠️ Niqob NUSXA OLINMAYDI — `ContactCard.PhoneValue`
                        bilan bir xil qoida: buferga tushgan
                        `+9989****567` hisobotda haqiqiy raqamdan farq
                        qilmasdi. */}
                    <Typography.Text code>{phone.shown}</Typography.Text>
                    <Tag color="warning">{t('contact.view.maskedTag')}</Tag>
                  </>
                ) : (
                  <>
                    <Typography.Text copyable>{phone.shown}</Typography.Text>
                    {phone.revealed ? (
                      <Tooltip title={t('subscriber.phoneRevealedHint')}>
                        <Tag color="success">{t('contact.view.revealedTag')}</Tag>
                      </Tooltip>
                    ) : null}
                  </>
                )}
                {phone.revealed ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeInvisibleOutlined />}
                    onClick={phone.hide}
                  >
                    {t('subscriber.phoneHide')}
                  </Button>
                ) : null}
              </Space>

              {/*
                ⚠️ OCHISH BOSHQARUVI — MAVJUD KOMPONENT (`ContactRevealControl`).
                U sabab maydonini, bo'sh sababda o'chiq tugmani va xato
                matnini o'zi boshqaradi. Ikkinchi nusxa yozilsa, biri
                «sababsiz ham bo'ladi» holatiga sirg'anib ketardi
                (o'sha komponent javadoc'idagi o'lchangan sabab).

                ⚠️ Faqat NIQOBLANGAN va huquq BOR bo'lganda. Huquq yo'q
                bo'lsa sabab aytiladi, tugma chizilmaydi.
              */}
              {phone.locked ? (
                phone.canReveal ? (
                  <ContactRevealControl
                    onReveal={phone.reveal}
                    revealing={phone.revealing}
                    revealError={phone.revealError}
                  />
                ) : (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('subscriber.phoneNoReveal')}
                  </Typography.Text>
                )
              ) : null}

              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {phone.locked
                  ? t('subscriber.phoneMaskedHint')
                  : t('subscriber.phoneShownHint')}
              </Typography.Text>
            </Space>
          ) : (
            <Typography.Text type="secondary">{t('subscriber.phoneMissing')}</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('subscribers.columns.kind')}>
          {t(`deliveryMethod.${subscriber.kind}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t('subscriber.visp.label')}>
          <Space size={8} wrap>
            <span>
              {subscriber.visp ? `${subscriber.visp.centerName ?? '—'} / ${subscriber.visp.name ?? '—'}` : '—'}
            </span>
            {canChangeVisp ? (
              <Button type="link" size="small" onClick={() => setChangingVisp(true)}>
                {t('subscriber.visp.change')}
              </Button>
            ) : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('branch.label')}>
          <Space size={8} wrap>
            {subscriber.branchNodeId ? (
              <span>{branchName ?? '…'}</span>
            ) : (
              <Typography.Text type="secondary">{t('branch.none')}</Typography.Text>
            )}
            {canChangeBranch ? (
              <Button type="link" size="small" onClick={() => setChangingBranch(true)}>
                {t('branch.change')}
              </Button>
            ) : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t('subscriber.agent.label')}>
          {/*
            ⚠️ BO'SH HOLAT XATO EMAS — na qizil, na ogohlantirish.
            «Biriktirilmagan» degani abonentni operator to'g'ridan-to'g'ri
            sotgan, va bu to'liq haqiqiy holat. Uni nuqson qilib
            ko'rsatish operatorni MAVJUD BO'LMAGAN muammoni tuzatishga
            yuborardi — bugungi bazada esa u 74 abonentning HAMMASIDA
            chiqardi, ya'ni ekran butunlay qizil bo'lardi va haqiqiy
            ogohlantirishlar shu shovqinda ko'rinmay qolardi.

            ⚠️ Lekin OQIBATI aytiladi (tooltipda emas, matnda):
            biriktirilmagan abonentning daromadi filial kesimida
            «taqsimlanmagan» savatiga tushadi. Buni aytmaslik hisobotda
            «nega hammasi taqsimlanmagan?» degan savolga javobsiz
            qoldirardi.
          */}
          {subscriber.agentId ? (
            <AgentRef agentId={subscriber.agentId} />
          ) : (
            <Typography.Text type="secondary">{t('subscriber.agent.none')}</Typography.Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('terms.simultaneousUseLabel')}>
          {/* `null` = siyosatdagi qiymat, individual chegara EMAS.
              "1" deb ko'rsatish yolg'on aniqlik bo'lardi */}
          {subscriber.simultaneousUse ?? t('deviceLimit.fromTariff')}
        </Descriptions.Item>
        {/* ⚠️ Yorliq o'rnida TAVSIF turardi (`terms.staticIp` — bir
            gaplik izoh). Qatorning nomi endi qisqa, izoh esa tooltip'da. */}
        <Descriptions.Item
          label={
            <Tooltip title={t('terms.staticIp')}>
              <span>{t('terms.staticIpLabel')}</span>
            </Tooltip>
          }
        >
          {subscriber.staticIp ?? t('limits.noStaticIp')}
        </Descriptions.Item>
        <Descriptions.Item label={t('subscriber.createdAt')}>
          {subscriber.createdAt ? (
            <Tooltip title={utcTooltip(subscriber.createdAt)}>
              {formatDateTime(subscriber.createdAt)}
            </Tooltip>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('subscriber.password')}>
          {/* Parol OCHIQ saqlanadi (MS-CHAPv2 talabi), lekin panelda
              KO'RSATILMAYDI — CLAUDE.md §7 aniq talabi. Faqat
              o'zgartirish mumkin */}
          {t('actions.password.currentNotShown')}
        </Descriptions.Item>
      </Descriptions>
      {canChangeVisp ? (
        <ChangeVispModal subscriber={subscriber} open={changingVisp} onClose={() => setChangingVisp(false)} />
      ) : null}
      {canChangeBranch ? (
        <ChangeBranchModal
          subscriber={subscriber}
          open={changingBranch}
          canClear={tenantWide}
          onClose={() => setChangingBranch(false)}
        />
      ) : null}
    </Card>
  )
}
